const pad = (n: number) => String(n).padStart(2, '0');

/** Format a Date object as a datetime-local string ("YYYY-MM-DDTHH:MM") in local timezone. */
export function formatDatetimeLocal(date: Date): string {
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Convert a PocketBase UTC date string (e.g. "2024-06-18 14:30:00.000Z") to a datetime-local
 *  string in the browser's local timezone, ready for an <input type="datetime-local">. */
export function pbDateToDatetimeLocal(pb: string | null | undefined): string {
	if (!pb) {
		return '';
	}
	const date = new Date(pb.replace(' ', 'T'));
	return isNaN(date.getTime()) ? '' : formatDatetimeLocal(date);
}

/** Convert a datetime-local string (local timezone, no timezone suffix) to a UTC ISO string
 *  suitable for PocketBase DateField. Returns null for empty/invalid input. */
export function datetimeLocalToIso(localDt: string): string | null {
	if (!localDt) {
		return null;
	}
	const date = new Date(localDt);
	return isNaN(date.getTime()) ? null : date.toISOString();
}

export function getRelativeTimeString(
  date: Date | number,
  lang = "de",
): string {
  const timeMs = typeof date === "number" ? date : new Date(date).getTime();
  const deltaSeconds = Math.round((timeMs - Date.now()) / 1000);
  const cutoffs = [
    60,
    3600,
    86400,
    86400 * 7,
    86400 * 30,
    86400 * 365,
    Infinity,
  ];
  const units: Intl.RelativeTimeFormatUnit[] = [
    "second",
    "minute",
    "hour",
    "day",
    "week",
    "month",
    "year",
  ];
  const unitIndex = cutoffs.findIndex(
    (cutoff) => cutoff > Math.abs(deltaSeconds),
  );
  const divisor = unitIndex ? cutoffs[unitIndex - 1] : 1;
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: "auto" });
  return rtf.format(Math.floor(deltaSeconds / divisor), units[unitIndex]);
}

export function getChatTimeStamp(date: Date | string, lang = "de") {
  const d = new Date(date);
  const now = new Date();

  // Get just the date parts for comparison (reset time to midnight)
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const inputDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());

  // Format time as HH:MM
  const timeStr = d.toLocaleTimeString(lang, {
    hour: "2-digit",
    minute: "2-digit",
  });

  if (inputDate.getTime() === today.getTime()) {
    // Today: just show time
    return timeStr;
  } else if (inputDate.getTime() === yesterday.getTime()) {
    // Yesterday: show "yesterday" + time
    const rtf = new Intl.RelativeTimeFormat(lang, { numeric: "auto" });
    const yesterdayStr = rtf.format(-1, "day");
    return `${yesterdayStr}, ${timeStr}`;
  } else {
    // Older: show date + time
    const dateStr = d.toLocaleDateString(lang);
    return `${dateStr}, ${timeStr}`;
  }
}

export function getFormattedDate(date: Date | string) {
  if (!date) {
    return null;
  }
  const d = new Date(date);
  const hasTime =
    d.getUTCHours() !== 0 || d.getUTCMinutes() !== 0 || d.getUTCSeconds() !== 0;
  if (hasTime) {
    return `${d.toLocaleDateString("de")}, ${d.toLocaleTimeString("de", { hour: "2-digit", minute: "2-digit" })}`;
  }
  return d.toLocaleDateString("de");
}
